import { useEffect, useRef, useState } from "react";

// Iter69 — Best-fix window in ms. Any sample older than this is
// dropped from the candidate pool so we don't pin to a stale fix
// when the user has moved across a building / a few blocks.
const BEST_FIX_WINDOW_MS = 30_000;

/**
 * useGeolocation — high-accuracy device geolocation with a sliding-
 * window "best fix" reducer.
 *
 * Returns:
 *   coords:      latest sample (may be coarse: cell-tower, WiFi)
 *   coords.best: smallest-accuracy sample from the last 30 s window;
 *                falls back to the latest if the window is empty
 *   error / permission / refresh
 *
 * The SOS button should ALWAYS prefer `coords.best || coords`. When
 * the citizen is walking around or moving between cells, the latest
 * GPS reading is often less precise than a 10 s-old one that hit GPS-
 * grade ±5 m accuracy. `coords.best` gives the dispatcher the most
 * trustworthy point we ever saw in this session.
 */
export function useGeolocation() {
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState("prompt"); // prompt | granted | denied
  // Rolling window of recent fixes (each entry has lat, lng, accuracy, ts).
  const windowRef = useRef([]);

  const _pickBest = (sample) => {
    const now = Date.now();
    const w = windowRef.current.filter((s) => (now - s.ts) <= BEST_FIX_WINDOW_MS);
    w.push(sample);
    windowRef.current = w;
    // Best = smallest accuracy radius (lower is better). Ties → most recent.
    let best = w[0];
    for (const s of w) {
      if (s.accuracy != null && (best.accuracy == null || s.accuracy < best.accuracy)) best = s;
    }
    return best;
  };

  const _absorb = (pos) => {
    const sample = {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      altitude: pos.coords.altitude,
      altitudeAccuracy: pos.coords.altitudeAccuracy,
      heading: pos.coords.heading,
      speed: pos.coords.speed,
      timestamp: pos.timestamp,
      ts: Date.now(),
    };
    const best = _pickBest(sample);
    setCoords({
      latitude: sample.latitude,
      longitude: sample.longitude,
      accuracy: sample.accuracy,
      altitude: sample.altitude,
      altitudeAccuracy: sample.altitudeAccuracy,
      heading: sample.heading,
      speed: sample.speed,
      timestamp: sample.timestamp,
      // `best` carries the same shape; consumers (SOS button) read
      // `coords.best || coords` for the most-trusted point.
      best: {
        latitude: best.latitude,
        longitude: best.longitude,
        accuracy: best.accuracy,
        altitude: best.altitude,
        altitudeAccuracy: best.altitudeAccuracy,
        heading: best.heading,
        speed: best.speed,
        timestamp: best.timestamp,
      },
    });
    setPermission("granted");
    setError(null);
  };

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      Promise.resolve().then(() => {
        setError("Geolocation is not supported by this browser.");
        setPermission("denied");
      });
      return undefined;
    }

    let watchId = null;

    const onError = (err) => {
      const code = err?.code;
      let msg = err?.message || "Unable to retrieve location";
      if (code === 1) msg = "denied";
      else if (code === 2) msg = "unavailable";
      else if (code === 3) msg = "timeout";
      setError(msg);
      if (code === 1) setPermission("denied");
    };

    navigator.geolocation.getCurrentPosition(_absorb, onError, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });

    watchId = navigator.geolocation.watchPosition(_absorb, onError, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 15000,
    });

    return () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  const refresh = () => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      _absorb,
      (err) => {
        const code = err?.code;
        let msg = err?.message || "unavailable";
        if (code === 1) { msg = "denied"; setPermission("denied"); }
        else if (code === 2) msg = "unavailable";
        else if (code === 3) msg = "timeout";
        setError(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  return { coords, error, permission, refresh };
}
